import React, { useEffect, useState, useMemo } from 'react';
import { useToggle, useFetch } from 'hooks';
import { Popover as UIPopover, ActionList, Skeleton } from 'ui';
import Popover from 'components/shared/popover';
import { routes } from 'constants/config';
import { TEXT_VARIANTS, Text } from 'components/ui/Typography';
import ApplyButton from 'components/shared/index/filters/apply_button';
import ScrollableContainer from 'components/shared/scrollable';
import {
  isArrayEqual,
  ToggleButton,
  getLabel,
} from 'components/shared/index/filters/multi_select';

const CombinedTagsFilter = props => {
  const [isActive, toggleIsActive] = useToggle(false);
  const [tagsIsActive, setTagsIsActive] = useState(false);
  const [skillTagsIsActive, setSkillTagsIsActive] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const lastSelectedOptions = useMemo(
    () => props.selected.tags || [],
    [props.selected.tags],
  );
  useEffect(() => {
    setSelectedOptions(props.selected.tags || []);
  }, [props.selected.tags]);

  const [selectedSkillTagOptions, setSelectedSkillTagOptions] = useState([]);
  const lastSelectedSkillTagOptions = useMemo(
    () => props.selected.skillTags || [],
    [props.selected.skillTags],
  );

  useEffect(() => {
    setSelectedSkillTagOptions(
      props?.selected?.skillTags?.map(tag => `${tag}`) || [],
    );
  }, [props.selected.skillTags]);

  const {
    data: skillTagData,
    loading,
    initialLoading,
  } = useFetch(`${routes.CONTENT.SKILL_TAGS}${window.location.search}`);

  const skillTags = useMemo(() => {
    if (initialLoading || !skillTagData) return [];
    return Object.entries(skillTagData)
      .map(tag => ({
        id: tag[0],
        ...tag[1],
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [initialLoading, skillTagData]);

  const hideResultCount = props.isAdvancedSearch;

  function getSortedKeys() {
    const order = props.sort.descending ? -1 : 1;
    return Object.keys(props.data)
      .map(keyName => {
        let value = props.data[keyName][props.sort.key];
        value = typeof value === 'string' ? value.toLowerCase() : value;
        return [parseInt(keyName, 10), value];
      })
      .sort((current, next) => (current[1] < next[1] ? order * -1 : order));
  }

  function handleOptionClick(key, type) {
    const options = type === 'tags' ? selectedOptions : selectedSkillTagOptions;
    const selectedOptionsSet = new Set(options);
    if (selectedOptionsSet.has(key)) {
      selectedOptionsSet.delete(key);
    } else {
      selectedOptionsSet.add(key);
    }
    if (type === 'tags') {
      setSelectedOptions(Array.from(selectedOptionsSet));
    } else {
      setSelectedSkillTagOptions(Array.from(selectedOptionsSet));
    }
  }
  const shouldApplyFilter = useMemo(() => {
    return !isArrayEqual(selectedOptions, lastSelectedOptions);
  }, [selectedOptions, lastSelectedOptions]);

  const shouldApplySkillTagsFilter = useMemo(() => {
    return !isArrayEqual(selectedSkillTagOptions, lastSelectedSkillTagOptions);
  }, [selectedSkillTagOptions, lastSelectedSkillTagOptions]);

  const combinedArray = () => {
    if (
      Array.isArray(selectedOptions) &&
      Array.isArray(selectedSkillTagOptions)
    ) {
      return [...selectedSkillTagOptions, ...selectedOptions];
    }

    if (Array.isArray(selectedOptions)) {
      return selectedOptions;
    }

    if (Array.isArray(selectedSkillTagOptions)) {
      return selectedSkillTagOptions;
    }

    return [];
  };

  return (
    <div className="IndexFilter IndexFilter-button">
      <UIPopover
        visible={isActive}
        onClose={() => {
          setTagsIsActive(false);
          setSkillTagsIsActive(false);
          toggleIsActive();
        }}
        activator={
          <ToggleButton
            isActive={isActive}
            buttonLabel={getLabel(combinedArray(), {
              plural: 'Tags',
              singular: 'Tag',
            })}
            toggleMenu={() => {
              toggleIsActive();
            }}
            {...props}
          />
        }
      >
        <div className="AppSwitcher-popover bdrs-4 f-o-n">
          <ActionList
            items={[
              {
                content: (
                  <>
                    <Text
                      variant={TEXT_VARIANTS.BODY}
                      className="ml-8 TagsSelectButton"
                    >
                      {t('tags.table.tabs.content')}
                    </Text>
                    {tagsIsActive && (
                      <Popover
                        styleName="CombinedTagsFilter--ContentTagsPopover"
                        dismissCallback={() => {
                          setTagsIsActive(false);
                        }}
                        showArrow={false}
                      >
                        <ScrollableContainer>
                          {getSortedKeys().map(key => (
                            <label
                              htmlFor={`select-option-${key[0]}`}
                              key={key[0]}
                              className="NewPopoverOption"
                            >
                              <input
                                id={`select-option-${key[0]}`}
                                checked={selectedOptions.indexOf(key[0]) > -1}
                                onChange={() =>
                                  handleOptionClick(key[0], 'tags')
                                }
                                type="checkbox"
                                onKeyDown={e =>
                                  e.key === 'Enter' &&
                                  handleOptionClick(key[0], 'tags')
                                }
                              />
                              <span className="NewPopoverOption-label TagsPopoverClick">
                                {props.data[key[0]].name}
                              </span>
                              {!hideResultCount && (
                                <span className="NewPopoverOption-count TagsPopoverClick">
                                  {props.data[key[0]].count}
                                </span>
                              )}
                            </label>
                          ))}
                        </ScrollableContainer>
                        <ApplyButton
                          onClick={() => {
                            if (shouldApplyFilter) {
                              const { applyCallback } = props;
                              applyCallback({
                                tags: selectedOptions,
                                skillTags: lastSelectedSkillTagOptions,
                              });
                              setTagsIsActive(false);
                              toggleIsActive();
                            }
                          }}
                          enabled={shouldApplyFilter}
                          accessible
                        />
                      </Popover>
                    )}
                  </>
                ),
                onClick: e => {
                  if (
                    e.target.classList.contains('ActionList-itemButton') ||
                    e.target.classList.contains('TagsSelectButton')
                  ) {
                    setTagsIsActive(true);
                  }
                },
              },
              {
                content: (
                  <>
                    <Text
                      variant={TEXT_VARIANTS.BODY}
                      className="ml-8 SkillsTagsSelectButton"
                    >
                      {t('tags.table.tabs.skill')}
                    </Text>
                    {skillTagsIsActive && (
                      <Popover
                        styleName="CombinedTagsFilter--SkillTagsPopover"
                        dismissCallback={() => {
                          setSkillTagsIsActive(false);
                        }}
                        showArrow={false}
                      >
                        {loading ? (
                          <>
                            <Skeleton className="mb-8" />
                            <Skeleton className="mb-8" width={150} />
                            <Skeleton />
                          </>
                        ) : (
                          <>
                            <ScrollableContainer>
                              {skillTags.map(tag => (
                                <label
                                  htmlFor={`select-option-${tag.id}`}
                                  key={tag.id}
                                  className="NewPopoverOption"
                                >
                                  <input
                                    id={`select-option-${tag.id}`}
                                    checked={
                                      selectedSkillTagOptions.indexOf(tag.id) >
                                      -1
                                    }
                                    onChange={() =>
                                      handleOptionClick(tag.id, 'skillTags')
                                    }
                                    type="checkbox"
                                    onKeyDown={e =>
                                      e.key === 'Enter' &&
                                      handleOptionClick(tag.id, 'skillTags')
                                    }
                                  />
                                  <span className="NewPopoverOption-label TagsPopoverClick">
                                    {tag.name}
                                  </span>
                                  {!hideResultCount && (
                                    <span className="NewPopoverOption-count TagsPopoverClick">
                                      {tag.count}
                                    </span>
                                  )}
                                </label>
                              ))}
                            </ScrollableContainer>
                            <ApplyButton
                              onClick={() => {
                                if (shouldApplySkillTagsFilter) {
                                  const { applyCallback } = props;
                                  applyCallback({
                                    tags: lastSelectedOptions,
                                    skillTags: selectedSkillTagOptions.map(sk =>
                                      parseInt(sk, 10),
                                    ),
                                  });
                                  setSkillTagsIsActive(false);
                                  toggleIsActive();
                                }
                              }}
                              enabled={shouldApplySkillTagsFilter}
                              accessible
                            />
                          </>
                        )}
                      </Popover>
                    )}
                  </>
                ),
                onClick: e => {
                  if (
                    e.target.classList.contains('ActionList-itemButton') ||
                    e.target.classList.contains('SkillsTagsSelectButton')
                  ) {
                    setSkillTagsIsActive(true);
                  }
                },
              },
            ]}
          />
        </div>
      </UIPopover>
    </div>
  );
};

export default CombinedTagsFilter;
